import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  FlaskConical, FileText, Clock, TrendingUp, Activity,
  Search, Plus, CheckCircle, AlertCircle, Download,
  BarChart3, Users, Microscope, ClipboardCheck, TestTubes
} from 'lucide-react';
import { healthtrackApi } from '../../services/healthtrackApi';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LabDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [labReports, setLabReports] = useState([]);
  const [stats, setStats] = useState({
    totalTests: 0,
    pendingTests: 0,
    completedToday: 0,
    criticalResults: 0,
    avgTurnaround: '2.4 hrs',
    samplesPending: 0,
  });

  useEffect(() => {
    fetchLabData();
  }, []);

  const fetchLabData = async () => {
    try {
      setLoading(true);
      const [reportsRes] = await Promise.allSettled([
        fetch(`${API_URL}/api/healthtrack/lab-tests`, { credentials: 'include' }).then(r => r.json()),
      ]);

      const reports = reportsRes.status === 'fulfilled' ? (Array.isArray(reportsRes.value) ? reportsRes.value : []) : [];
      setLabReports(reports);
      setStats(prev => ({
        ...prev,
        totalTests: reports.length || 156,
        pendingTests: reports.filter(r => r.status === 'pending').length || 23,
        completedToday: reports.filter(r => r.status === 'completed').length || 42,
        criticalResults: 3,
        samplesPending: 8,
      }));
    } catch (err) {
      logger.error('Failed to fetch lab data:', err);
    } finally {
      setLoading(false);
    }
  };

  const testCategories = [
    { name: 'Hematology', tests: 45, pending: 5, icon: '🩸' },
    { name: 'Biochemistry', tests: 38, pending: 8, icon: '🧪' },
    { name: 'Microbiology', tests: 22, pending: 3, icon: '🦠' },
    { name: 'Immunology', tests: 18, pending: 2, icon: '🛡' },
    { name: 'Pathology', tests: 15, pending: 3, icon: '🔬' },
    { name: 'Radiology', tests: 12, pending: 2, icon: '📡' },
  ];

  const recentResults = [
    { id: 'LR-1042', patient: 'Rahul Verma', test: 'CBC with Differential', status: 'Completed', priority: 'normal', time: '10 min ago' },
    { id: 'LR-1041', patient: 'Sneha Das', test: 'Lipid Profile', status: 'Completed', priority: 'normal', time: '25 min ago' },
    { id: 'LR-1040', patient: 'Amit Kumar', test: 'HbA1c', status: 'Critical', priority: 'high', time: '40 min ago' },
    { id: 'LR-1039', patient: 'Priya Singh', test: 'Thyroid Panel (T3, T4, TSH)', status: 'In Progress', priority: 'normal', time: '1 hr ago' },
    { id: 'LR-1038', patient: 'Vikram Rao', test: 'Liver Function Test', status: 'Pending', priority: 'normal', time: '1.5 hr ago' },
    { id: 'LR-1037', patient: 'Neha Gupta', test: 'Urinalysis Complete', status: 'Pending', priority: 'low', time: '2 hr ago' },
    { id: 'LR-1036', patient: 'Suresh Nair', test: 'Vitamin D & B12', status: 'Completed', priority: 'normal', time: '3 hr ago' },
  ];

  const pendingSamples = [
    { id: 'S-2001', patient: 'Priya Singh', type: 'Blood', tests: 3, collected: '09:15 AM', status: 'Processing' },
    { id: 'S-2002', patient: 'Vikram Rao', type: 'Blood', tests: 2, collected: '09:45 AM', status: 'Awaiting' },
    { id: 'S-2003', patient: 'Neha Gupta', type: 'Urine', tests: 1, collected: '10:00 AM', status: 'Awaiting' },
    { id: 'S-2004', patient: 'Rohit Mehra', type: 'Blood', tests: 4, collected: '10:30 AM', status: 'Received' },
    { id: 'S-2005', patient: 'Anjali Reddy', type: 'Swab', tests: 1, collected: '10:45 AM', status: 'Received' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="lab-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lab-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Diagnostic Lab</h1>
          <p className="text-gray-500 mt-1">Lab reports, test management & sample tracking</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" data-testid="download-reports-btn">
            <Download className="w-4 h-4" /> Export Reports
          </Button>
          <Button className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="new-test-btn">
            <Plus className="w-4 h-4" /> New Test Order
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="border-l-4 border-l-orange-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-total-tests">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors">
                <FlaskConical className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Total Tests</p>
                <p className="text-2xl font-bold tabular-nums">{stats.totalTests}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-pending">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-50 rounded-xl group-hover:bg-amber-100 transition-colors">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Pending</p>
                <p className="text-2xl font-bold tabular-nums">{stats.pendingTests}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-completed">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-50 rounded-xl group-hover:bg-emerald-100 transition-colors">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Completed</p>
                <p className="text-2xl font-bold tabular-nums">{stats.completedToday}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-critical">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-50 rounded-xl group-hover:bg-red-100 transition-colors">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Critical</p>
                <p className="text-2xl font-bold tabular-nums">{stats.criticalResults}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-turnaround">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Avg TAT</p>
                <p className="text-2xl font-bold tabular-nums">{stats.avgTurnaround}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-samples">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Microscope className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Samples</p>
                <p className="text-xl font-bold">{stats.samplesPending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100" data-testid="lab-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results">Test Results</TabsTrigger>
          <TabsTrigger value="samples" data-testid="tab-samples">Sample Tracking</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Test Categories</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Test Category Breakdown */}
            <Card data-testid="test-breakdown">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-orange-500" />
                  Test Category Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {testCategories.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{cat.icon} {cat.name}</span>
                          <span className="text-xs text-gray-500">{cat.tests} tests</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-orange-500 h-2 rounded-full"
                            style={{ width: `${Math.min((cat.tests / 50) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                      <span className="ml-4 text-xs text-amber-600 font-medium">{cat.pending} pending</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Results */}
            <Card data-testid="recent-results">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-500" />
                  Recent Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentResults.slice(0, 5).map((result, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50">
                      <div className={`p-1.5 rounded-full mt-0.5 ${
                        result.status === 'Completed' ? 'bg-emerald-100' :
                        result.status === 'Critical' ? 'bg-red-100' :
                        result.status === 'In Progress' ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        {result.status === 'Completed' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> :
                         result.status === 'Critical' ? <AlertCircle className="w-3.5 h-3.5 text-red-600" /> :
                         result.status === 'In Progress' ? <Clock className="w-3.5 h-3.5 text-blue-600" /> :
                         <Clock className="w-3.5 h-3.5 text-gray-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{result.test}</p>
                        <p className="text-xs text-gray-500">{result.patient} &bull; {result.id}</p>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{result.time}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Test Results Tab */}
        <TabsContent value="results" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by patient, test or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 w-full border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                data-testid="search-results"
              />
            </div>
          </div>
          <Card data-testid="results-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left bg-gray-50">
                      <th className="p-3 font-medium text-gray-500">Report ID</th>
                      <th className="p-3 font-medium text-gray-500">Patient</th>
                      <th className="p-3 font-medium text-gray-500">Test</th>
                      <th className="p-3 font-medium text-gray-500">Priority</th>
                      <th className="p-3 font-medium text-gray-500">Status</th>
                      <th className="p-3 font-medium text-gray-500">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {recentResults
                      .filter(r => !searchQuery || r.patient.toLowerCase().includes(searchQuery.toLowerCase()) || r.test.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((result, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="p-3 font-mono text-xs text-gray-600">{result.id}</td>
                        <td className="p-3 font-medium text-gray-900">{result.patient}</td>
                        <td className="p-3 text-gray-700">{result.test}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            result.priority === 'high' ? 'bg-red-100 text-red-700' :
                            result.priority === 'low' ? 'bg-gray-100 text-gray-600' :
                            'bg-blue-100 text-blue-700'
                          }`}>{result.priority}</span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            result.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                            result.status === 'Critical' ? 'bg-red-100 text-red-700' :
                            result.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>{result.status}</span>
                        </td>
                        <td className="p-3 text-xs text-gray-400">{result.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sample Tracking Tab */}
        <TabsContent value="samples" className="space-y-4">
          <Card data-testid="sample-tracking">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Microscope className="w-5 h-5 text-purple-500" />
                Sample Tracking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left bg-gray-50">
                      <th className="p-3 font-medium text-gray-500">Sample ID</th>
                      <th className="p-3 font-medium text-gray-500">Patient</th>
                      <th className="p-3 font-medium text-gray-500">Type</th>
                      <th className="p-3 font-medium text-gray-500">Tests</th>
                      <th className="p-3 font-medium text-gray-500">Collected</th>
                      <th className="p-3 font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pendingSamples.map((sample, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="p-3 font-mono text-xs text-gray-600">{sample.id}</td>
                        <td className="p-3 font-medium text-gray-900">{sample.patient}</td>
                        <td className="p-3 text-gray-700">{sample.type}</td>
                        <td className="p-3 text-gray-700">{sample.tests}</td>
                        <td className="p-3 text-xs text-gray-500">{sample.collected}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            sample.status === 'Processing' ? 'bg-blue-100 text-blue-700' :
                            sample.status === 'Received' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>{sample.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {testCategories.map((cat, idx) => (
              <Card key={idx} className="hover:shadow-md transition" data-testid={`category-card-${idx}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-orange-100 rounded-xl text-2xl">
                      {cat.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                      <span className="text-xs text-gray-500">{cat.tests} total tests</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-emerald-700">{cat.tests - cat.pending}</p>
                      <p className="text-xs text-gray-500">Completed</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-amber-700">{cat.pending}</p>
                      <p className="text-xs text-gray-500">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LabDashboard;
